/**
 * 产业链节点/边的语义判定与小工具
 *
 * 与 chain-constants.ts 配套——常量在那里，逻辑在这里。
 * 这些函数在多个组件中被复用（旧 ChainMap、新 SkillTreeMap、SummaryPanel、DetailPanel）。
 */

import type { ChainNode, ChainEdge } from '../types';

/**
 * 收费站节点（Toll Station）：高瓶颈 + 描述里命中"稀缺/垄断/唯一"关键字。
 * 视觉上用金色光环 + 王冠图标突出显示。
 */
export function isTollStation(node: ChainNode): boolean {
  return node.bottleneck_level === 'high' &&
    !!(node.bottleneck_reason?.includes('稀缺') ||
      node.bottleneck_reason?.includes('垄断') ||
      node.bottleneck_reason?.includes('唯一'));
}

/**
 * 稀缺节点：高瓶颈但还不到收费站标准（关键字未命中）。
 * 视觉上用紫色微光区分。
 */
export function isScarceNode(node: ChainNode): boolean {
  return node.bottleneck_level === 'high' && !isTollStation(node);
}

/**
 * 不可替代节点：相关公司 ≤ 2 家。
 * 视觉上用锁形图标（与收费站王冠互斥，王冠优先）。
 */
export function isIrreplaceableNode(node: ChainNode): boolean {
  return (node.companies?.length ?? 0) <= 2;
}

/**
 * 旧版 ReactFlow 节点的最小宽度公式，根据公司数和稀缺标记伸缩。
 * 仅在 ChainMap.tsx（旧地图）使用；M5 重写后可考虑删除或迁移到 SkillTreeNode 直径公式。
 */
export function getNodeMinWidth(companyCount: number, scarce: boolean): number {
  const base = 80;
  const extra = Math.min(companyCount * 8, 60);
  const scarceBonus = scarce ? 10 : 0;
  return base + extra + scarceBonus;
}

/**
 * "简化视图"模式下应可见的节点 id 集合：
 *   - 全部高瓶颈节点（始终显示）
 *   - 选中节点本身
 *   - 选中节点的直接邻居（1 跳）
 */
export function computeVisibleIds(
  selectedNode: string | null,
  nodes: ChainNode[],
  edges: ChainEdge[],
): Set<string> {
  const visibleIds = new Set<string>();
  for (const n of nodes) {
    if (n.bottleneck_level === 'high') visibleIds.add(n.id);
  }
  if (selectedNode) {
    visibleIds.add(selectedNode);
    for (const e of edges) {
      if (e.source === selectedNode) visibleIds.add(e.target);
      if (e.target === selectedNode) visibleIds.add(e.source);
    }
  }
  return visibleIds;
}

/**
 * 给定节点的上游/下游节点列表（基于 edges 中的 source/target 反查 nodes）。
 * DetailPanel 在节点选中时用它展示上下游导航条。
 */
export function getUpstreamDownstream(
  nodeId: string,
  edges: ChainEdge[],
  nodes: ChainNode[],
): { upstream: ChainNode[]; downstream: ChainNode[] } {
  const nodeMap = new Map(nodes.map(n => [n.id, n]));
  const upstream: ChainNode[] = [];
  const downstream: ChainNode[] = [];
  for (const e of edges) {
    if (e.target === nodeId && nodeMap.has(e.source)) upstream.push(nodeMap.get(e.source)!);
    if (e.source === nodeId && nodeMap.has(e.target)) downstream.push(nodeMap.get(e.target)!);
  }
  return { upstream, downstream };
}
